import { CommonModule } from "@angular/common";
import {
  NgModule,
  Input,
  Component,
  Directive,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
  OnInit,
  Inject,
  LOCALE_ID,
} from "@angular/core";

import {
  chunk,
  isAfter,
  isBefore,
  addYears,
  eachMonthOfInterval,
  endOfYear,
  InputName,
  startOfYear,
  subYears,
  isSameDay,
  startOfDecade,
  endOfDecade,
  eachYearOfInterval,
  getMonth,
  startOfMonth,
  startOfISOWeek,
  pipe,
  endOfDay,
  endOfMonth,
  endOfISOWeek,
  addMonths,
  subMonths,
  eachDayOfInterval,
  startOfDay,
  isValidSwedishWeekday,
  isValidFinnishWeekday,
  isWeekend,
  isSameYear,
  isSameMonth,
} from "@vattenfall/util";

import { InputsModule } from "./inputs";

/** Callback functions used for navigating different calendar sections. */
export type InputCalendarDirectiveCallbacks = {
  /** To to next panel for calendar section, i.e. next month. */
  nextCallback: (date: Date, amount: number) => Date;
  /** To to previous panel for calendar section, i.e. next month. */
  previousCallback: (date: Date, amount: number) => Date;
  /** To extract the first millisecond of a date unit (day, week, month, year) in the current calendar section. */
  startDateUnitCallback: (date: Date) => Date;
  /** To extract the first date in a range of dates to display in the current calendar section. */
  startDateCallback: (date: Date) => Date;
  /** To extract the last date in a range of dates to display in the current calendar section. */
  endDateCallback: (date: Date) => Date;
  /** To extract the last millisecond of a date unit (day, week, month, year) in the current calendar section. */
  endDateUnitCallback: (date: Date) => Date;
  /** To generate a range of dates between the start and end date, i.e. a range of months. */
  intervalCallback: (interval: Interval) => Array<Date>;
};

/** Shared properties used to communicating between different calendar components. */
@Directive()
export class InputCalendarDirective {
  /** The type of calendar. */
  @Input() type: InputName | null = null;
  /** The type of calendar currently displayed. */
  @Input() activeType: InputName | null = null;
  /** The earliest date to accept for `value`. */
  @Input() min: Date | null = null;
  /** The latest date to accept for `value`. */
  @Input() max: Date | null = null;
  /** Date selected by user. */
  @Input() value: Date | null = null;
  /** Date displayed in panel. */
  @Input() activeDate = startOfDay(new Date());
  /** Disables all non-weekdays in calendar from user interaction. */
  @Input() weekday = false;
  /** Emits every time user selects a new date. */
  @Output() readonly valueChange = new EventEmitter<Date | null>();
  /** Emits every time the panel change date. */
  @Output() readonly activeDateChange = new EventEmitter<Date>();
}

/** Shared utilities for all calendar sections. */
@Directive()
export class InputCalendarSectionDirective extends InputCalendarDirective {
  constructor(protected callbacks: InputCalendarDirectiveCallbacks) {
    super();
  }
  /** Whether the provided date is higher or lower than current `min` and `max` dates. */
  isDisabled(date: Date) {
    return !this.isWithinLowerBound(date) || !this.isWithinHigherBound(date);
  }
  /** Whether the provided date is lower than current `min` date. */
  isWithinLowerBound(date?: Date) {
    if (!date || !this.min) return true;
    return isAfter(this.callbacks.endDateUnitCallback(date), this.callbacks.startDateUnitCallback(this.min));
  }
  /** Whether the provided date is higher than current `max` date. */
  isWithinHigherBound(date?: Date) {
    if (!date || !this.max) return true;
    return isBefore(this.callbacks.startDateUnitCallback(date), this.callbacks.endDateUnitCallback(this.max));
  }
  /** Whether the provided date is the current selected date. */
  isSelected(date: Date) {
    return this.value && isSameDay(date, this.value);
  }
  /** Whether the provided date is the current active date. */
  isActive(date: Date) {
    return this.value && isSameDay(date, this.activeDate);
  }
  /** Range of dates displayed in the current calendar panel. */
  get dates() {
    const start = this.callbacks.startDateCallback(this.activeDate);
    const end = this.callbacks.endDateCallback(this.activeDate);
    return this.callbacks.intervalCallback({ start, end });
  }
  /** Breaks range of dates into rows */
  getRows(columnCount: number) {
    return chunk(this.dates, columnCount);
  }
  /** Change panel to later range of dates. */
  onNext() {
    this.activeDateChange.emit(this.callbacks.nextCallback(this.activeDate, 1));
  }
  /** Change panel to earlier range of dates. */
  onPrevious() {
    this.activeDateChange.emit(this.callbacks.previousCallback(this.activeDate, 1));
  }
  /** Method invoked when the user selects a date from calendar. */
  onSelected(date: Date, event?: Event) {
    event?.preventDefault();
    if (!this.isDisabled(date) && !this.isSelected(date)) {
      this.activeDateChange.emit(date);
      this.valueChange.emit(date);
    }
  }
  /** Track function for *ngFor loop (to ensure rerender when `dates` list change). */
  trackBy(index: number) {
    return index;
  }
}

@Component({
  selector: "ui-input-calendar",
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section
      [style.width]="fluid ? null : '279px'"
      class="ds-border ds-border-tertiary-lightgrey ds-p-sm ds-w-full ds-rounded-md ds-mt-xs ds-shadow-md ds-text-center ds-absolute ds-z-50 ds-bg-primary-white"
    >
      <header class="ds-mt-xs ds-mb-xs" *ngIf="type !== 'year'">
        <label class="ds-cursor-pointer" [class.ds-cursor-not-allowed]="isYearDisabled">
          <input
            type="checkbox"
            class="ds-mx-2xs ds-appearance-none ds-peer"
            [disabled]="isYearDisabled"
            [checked]="activeType === 'year'"
            (change)="onActiveType('year')"
          />
          {{ activeDate | date: "yyyy" }}
          <span
            [class.before:!ds-bg-tertiary-lightgrey]="isYearDisabled"
            class="ds-icons-down peer-checked:!ds-icons-up peer-focus:before:!ds-bg-primary-blue"
          ></span>
        </label>
        <label *ngIf="type !== 'month'" class="ds-cursor-pointer" [class.ds-cursor-not-allowed]="isMonthDisabled">
          <input
            type="checkbox"
            class="ds-mx-2xs ds-appearance-none ds-peer"
            [disabled]="isMonthDisabled"
            [checked]="activeType === 'month'"
            (change)="onActiveType('month')"
          />
          {{ activeDate | date: "MMMM" }}
          <span
            [class.before:!ds-bg-tertiary-lightgrey]="isMonthDisabled"
            class="ds-icons-down peer-checked:!ds-icons-up peer-focus:before:!ds-bg-primary-blue"
          ></span>
        </label>
      </header>
      <ng-container [ngSwitch]="activeType">
        <ui-input-calendar-section-date
          *ngSwitchCase="'date'"
          type="date"
          [min]="min"
          [max]="max"
          [value]="value"
          [weekday]="weekday"
          [(activeDate)]="activeDate"
          (valueChange)="onValue($event!)"
        ></ui-input-calendar-section-date>
        <ui-input-calendar-section-week
          *ngSwitchCase="'week'"
          type="week"
          [min]="min"
          [max]="max"
          [value]="value"
          [weekday]="weekday"
          [(activeDate)]="activeDate"
          (valueChange)="onValue($event!)"
        ></ui-input-calendar-section-week>
        <ui-input-calendar-section-month
          *ngSwitchCase="'month'"
          type="month"
          [min]="min"
          [max]="max"
          [value]="value"
          [(activeDate)]="activeDate"
          (valueChange)="onValue($event!)"
        ></ui-input-calendar-section-month>
        <ui-input-calendar-section-year
          *ngSwitchCase="'year'"
          type="year"
          [min]="min"
          [max]="max"
          [value]="value"
          [(activeDate)]="activeDate"
          (valueChange)="onValue($event!)"
        ></ui-input-calendar-section-year>
      </ng-container>
    </section>
  `,
})
export class InputCalendarComponent extends InputCalendarDirective implements OnInit {
  @Input() fluid = false;

  ngOnInit() {
    this.activeDate = this.adjustDateToAllowedRange(this.activeDate);
    this.activeType = this.type;
  }
  get isYearDisabled() {
    return this.min && this.max && isSameYear(this.min, this.max);
  }
  get isMonthDisabled() {
    return this.min && this.max && isSameMonth(this.min, this.max);
  }
  onActiveType(selectedType: InputName) {
    this.activeType = this.activeType === selectedType ? this.type : selectedType;
  }
  onValue(date: Date) {
    date = this.adjustDateToAllowedRange(date);
    // Reset the calendar panel to the base after you have selected a year for example
    this.activeType = this.type;
    this.valueChange.emit(date);
  }

  /**
   * This function is used to adjust the active date when calendar opens or a value has been selected.
   * @param date - the date to be adjusted
   * When calendar opens or a value has been selected:
   * If current date is less then min date, move calendar panel to min date.
   * If current date is greater than max date, move calendar panel to max date.
   * Otherwise, move calendar panel to current date if neither min nor max is specified.
   * @returns date
   */
  adjustDateToAllowedRange(date: Date) {
    if (this.min) date = isBefore(this.activeDate, this.min) ? this.min : this.activeDate;
    else if (this.max) date = isAfter(this.activeDate, this.max) ? this.max : this.activeDate;
    return date;
  }
}

@Component({
  selector: "ui-input-calendar-footer",
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <footer class="ds-my-2xs">
      <button
        [class.ds-cursor-not-allowed]="!isPreviousActive"
        [class.before:!ds-bg-tertiary-lightgrey]="!isPreviousActive"
        [disabled]="!isPreviousActive"
        (click)="previousChange.emit(); $event.preventDefault()"
        class="ds-icons-left peer-checked:!ds-icons-left peer-focus:before:!ds-bg-primary-blue ds-outline-none ds-mr"
      ></button>
      <button
        [class.ds-cursor-not-allowed]="!isNextActive"
        [class.before:!ds-bg-tertiary-lightgrey]="!isNextActive"
        [disabled]="!isNextActive"
        (click)="nextChange.emit(); $event.preventDefault()"
        class="ds-icons-right peer-focus:before:!ds-bg-primary-blue ds-outline-none ds-ml"
      ></button>
    </footer>
  `,
})
export class InputCalendarFooterComponent {
  @Input() isNextActive!: boolean;
  @Input() isPreviousActive!: boolean;
  @Output() readonly nextChange = new EventEmitter();
  @Output() readonly previousChange = new EventEmitter();
}

@Component({
  selector: "ui-input-calendar-section-date",
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <table class="ds-calendar">
      <thead>
        <tr>
          <th></th>
          <th *ngFor="let date of dates | slice: 0:7">{{ date | date: "EEEEE" }}</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let week of getRows(7); trackBy: trackBy">
          <td class="ds-disabled">{{ week[0] | date: "w" }}</td>
          <td
            *ngFor="let date of week"
            (click)="onSelected(date)"
            (keydown.space)="onSelected(date, $event)"
            (keydown.enter)="onSelected(date, $event)"
            [attr.tabindex]="isActive(date) ? 1 : 0"
            [class.ds-disabled]="isDisabled(date)"
            [class.ds-selected]="isSelected(date)"
          >
            {{ date | date: "d" }}
          </td>
        </tr>
      </tbody>
    </table>
    <ui-input-calendar-footer
      [isNextActive]="isWithinHigherBound(dates.pop())"
      [isPreviousActive]="isWithinLowerBound(dates.shift())"
      (nextChange)="onNext()"
      (previousChange)="onPrevious()"
    ></ui-input-calendar-footer>
  `,
})
export class InputCalendarSectionDateComponent extends InputCalendarSectionDirective {
  constructor(@Inject(LOCALE_ID) private locale: string) {
    super({
      startDateUnitCallback: startOfDay,
      endDateUnitCallback: endOfDay,
      startDateCallback: pipe(startOfMonth, startOfISOWeek),
      endDateCallback: pipe(endOfMonth, endOfISOWeek),
      nextCallback: addMonths,
      previousCallback: subMonths,
      intervalCallback: eachDayOfInterval,
    });
  }
  override isDisabled(date: Date) {
    const isDisabled = super.isDisabled(date) || getMonth(date) !== getMonth(this.activeDate);
    if (this.weekday)
      switch (this.locale.slice(0, 2)) {
        case "sv":
          return isDisabled || !isValidSwedishWeekday(date);
        case "fi":
          return isDisabled || !isValidFinnishWeekday(date);
        default:
          return isDisabled || isWeekend(date);
      }
    return isDisabled;
  }
}

@Component({
  selector: "ui-input-calendar-section-week",
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <table class="ds-calendar">
      <thead>
        <tr>
          <th></th>
          <th *ngFor="let date of dates | slice: 0:7">{{ date | date: "EEEEE" }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          *ngFor="let week of getRows(7); trackBy: trackBy"
          [attr.tabindex]="isActive(week[0]) ? 1 : 0"
          [class.ds-selected]="isSelected(week[0])"
          (click)="onSelected(week[0])"
          (keydown.space)="onSelected(week[0], $event)"
          (keydown.enter)="onSelected(week[0], $event)"
        >
          <td class="ds-disabled">{{ week[0] | date: "w" }}</td>
          <td *ngFor="let date of week" [class.ds-disabled]="isDisabled(date)">
            {{ date | date: "d" }}
          </td>
        </tr>
      </tbody>
    </table>
    <ui-input-calendar-footer
      [isNextActive]="isWithinHigherBound(dates.pop())"
      [isPreviousActive]="isWithinLowerBound(dates.shift())"
      (nextChange)="onNext()"
      (previousChange)="onPrevious()"
    ></ui-input-calendar-footer>
  `,
})
export class InputCalendarSectionWeekComponent extends InputCalendarSectionDirective {
  constructor(@Inject(LOCALE_ID) private locale: string) {
    super({
      startDateUnitCallback: startOfISOWeek,
      endDateUnitCallback: endOfISOWeek,
      startDateCallback: pipe(startOfMonth, startOfISOWeek),
      endDateCallback: pipe(endOfMonth, endOfISOWeek),
      nextCallback: addMonths,
      previousCallback: subMonths,
      intervalCallback: eachDayOfInterval,
    });
  }
  override isDisabled(date: Date) {
    const isDisabled = super.isDisabled(date) || getMonth(date) !== getMonth(this.activeDate);
    if (this.weekday)
      switch (this.locale.slice(0, 2)) {
        case "sv":
          return isDisabled || !isValidSwedishWeekday(date);
        case "fi":
          return isDisabled || !isValidFinnishWeekday(date);
        default:
          return isDisabled || isWeekend(date);
      }
    return isDisabled;
  }
  override onSelected(date: Date, event?: Event) {
    event?.preventDefault();
    if (!this.isSelected(date)) {
      this.activeDateChange.emit(date);
      this.valueChange.emit(date);
    }
  }
}

@Component({
  selector: "ui-input-calendar-section-month",
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <table class="ds-calendar">
      <tbody>
        <tr *ngFor="let row of getRows(3); trackBy: trackBy">
          <td
            *ngFor="let date of row"
            [attr.tabindex]="isActive(date) ? 1 : 0"
            [class.ds-disabled]="isDisabled(date)"
            [class.ds-selected]="isSelected(date)"
            (click)="onSelected(date)"
            (keydown.space)="onSelected(date, $event)"
            (keydown.enter)="onSelected(date, $event)"
          >
            {{ date | date: "MMMM" }}
          </td>
        </tr>
      </tbody>
    </table>
    <ui-input-calendar-footer
      [isNextActive]="isWithinHigherBound(dates.pop())"
      [isPreviousActive]="isWithinLowerBound(dates.shift())"
      (nextChange)="onNext()"
      (previousChange)="onPrevious()"
    ></ui-input-calendar-footer>
  `,
})
export class InputCalendarSectionMonthComponent extends InputCalendarSectionDirective {
  constructor() {
    super({
      startDateUnitCallback: startOfMonth,
      endDateUnitCallback: endOfMonth,
      startDateCallback: startOfYear,
      endDateCallback: endOfYear,
      nextCallback: addYears,
      previousCallback: subYears,
      intervalCallback: eachMonthOfInterval,
    });
  }
}

@Component({
  selector: "ui-input-calendar-section-year",
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <table class="ds-calendar">
      <tbody>
        <tr *ngFor="let row of getRows(3); trackBy: trackBy">
          <td
            *ngFor="let date of row"
            [attr.tabindex]="isActive(date) ? 1 : 0"
            [class.ds-disabled]="isDisabled(date)"
            [class.ds-selected]="isSelected(date)"
            (click)="onSelected(date)"
            (keydown.space)="onSelected(date, $event)"
            (keydown.enter)="onSelected(date, $event)"
          >
            {{ date | date: "yyyy" }}
          </td>
        </tr>
      </tbody>
    </table>
    <ui-input-calendar-footer
      [isNextActive]="isWithinHigherBound(dates.pop())"
      [isPreviousActive]="isWithinLowerBound(dates.shift())"
      (nextChange)="onNext()"
      (previousChange)="onPrevious()"
    ></ui-input-calendar-footer>
  `,
})
export class InputCalendarSectionYearComponent extends InputCalendarSectionDirective {
  constructor() {
    super({
      startDateUnitCallback: startOfYear,
      endDateUnitCallback: endOfYear,
      startDateCallback: startOfDecade,
      endDateCallback: endOfDecade,
      nextCallback: (date: Date) => addYears(startOfDecade(date), 10),
      previousCallback: (date: Date) => subYears(endOfDecade(date), 10),
      intervalCallback: eachYearOfInterval,
    });
  }
}

/**
 * The `DatesModule` contains utilities for date input components.
 */
@NgModule({
  declarations: [
    InputCalendarComponent,
    InputCalendarFooterComponent,
    InputCalendarSectionDateComponent,
    InputCalendarSectionWeekComponent,
    InputCalendarSectionMonthComponent,
    InputCalendarSectionYearComponent,
  ],
  imports: [CommonModule, InputsModule],
  exports: [
    InputCalendarComponent,
    InputCalendarFooterComponent,
    InputCalendarSectionDateComponent,
    InputCalendarSectionWeekComponent,
    InputCalendarSectionMonthComponent,
    InputCalendarSectionYearComponent,
  ],
})
export class DatesModule {}
